import React from 'react';
import { Box, Typography, TextField, Tooltip } from '@mui/material';
import { red, orange, green } from '@mui/material/colors';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const getColumns = ({ handleItemOpenModal, handleQtyChange, userPersonalization }) => {
  const getPersonalizedValue = (key) => {
    const match = userPersonalization?.find((p) => p.personalized_element === key);
    return match?.personalized_value;
  };

  const YourQtyHeader = getPersonalizedValue('YourQtyHeader') || 'Your Qty';

  return [
    {
      header: 'Order Qty',
      accessorKey: 'suggested_qty',
      size: 75,
      Cell: ({ renderedCellValue, row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            variant='outlined'
            size='small'
            value={renderedCellValue}
            onChange={(e) => handleQtyChange(row.original.item_number, e.target.value)}
            inputProps={{
              style: { width: '60px', textAlign: 'center' },
              type: 'number',
            }}
          />
        </Box>
      ),
    },
    {
      header: YourQtyHeader,
      accessorKey: 'dealer_quantity',
      size: 75,
      Cell: ({ renderedCellValue }) => {
        let textColor;
        let backgroundColor;
        if (renderedCellValue == 0) {
          textColor = red[700];
          backgroundColor = red[100];
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              textAlign: 'center',
              backgroundColor,
              p: 1,
              borderRadius: 1,
            }}
          >
            <Typography variant='body1' sx={{ fontWeight: 'bold', color: textColor }}>
              {renderedCellValue}
            </Typography>
          </Box>
        );
      },
    },
    
    {
      header: 'Last Ordered',
      accessorKey: 'last_ordered_days',
      size: 100,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.last_ordered_days;
        const b = rowB.original.last_ordered_days;
        if (a === null && b === null) return 0;
        if (a === null) return 1;
        if (b === null) return -1;
        return a - b;
      },
      Cell: ({ renderedCellValue }) => {
        let textColor;
        let backgroundColor;
        let displayValue;
        if (renderedCellValue === null || renderedCellValue === undefined) {
          displayValue = 'No Sales Data';
          textColor = 'inherit';
        } else if (renderedCellValue === 'N/A') {
          displayValue = 'N/A';
          textColor = 'inherit';
        } else if (renderedCellValue == 0) {
          displayValue = 'Ordered Today';
          textColor = red[700];
          backgroundColor = red[100];
        } else if (renderedCellValue == 1) {
          displayValue = '1 Day Ago';
          textColor = red[700];
          backgroundColor = red[100];
        } else {
          displayValue = `${renderedCellValue} Days Ago`;
          if (renderedCellValue <= 3) {
            textColor = red[700];
            backgroundColor = red[100];
          } else if (renderedCellValue >= 4 && renderedCellValue <= 11) {
            textColor = orange[700];
            backgroundColor = orange[100];
          } else {
            textColor = 'inherit';
          }
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor,
              p: 1,
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant='body1' sx={{ fontWeight: 'bold', color: textColor }}>
              {displayValue}
            </Typography>
          </Box>
        );
      },
    },
    {
      header: 'Sales Velocity',
      accessorKey: 'qty_sold_last_30_days',
      size: 110,
      Cell: ({ renderedCellValue }) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='body1'>{renderedCellValue}</Typography>
        </Box>
      ),
    },
    
    {
      header: 'SS Qty',
      accessorKey: 'quantity_on_hand',
      size: 75,
      Cell: ({ renderedCellValue }) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='body1'>{renderedCellValue}</Typography>
        </Box>
      ),
    },
    {
      header: 'Type',
      accessorKey: 'item_type',
      size: 100,
      Cell: ({ renderedCellValue }) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='body1'>{renderedCellValue}</Typography>
        </Box>
      ),
    },
    {
      header: 'Description',
      accessorKey: 'description',
      size: 300,
      Cell: ({ renderedCellValue, row }) => {
        const itemNumber = row.original.item_number;
        const upc = row.original.upc;
        const itemLabel = itemNumber ?? 'N/A';
        const upcLabel = upc ?? 'N/A';

        return (
          <Box
            onClick={() => handleItemOpenModal(itemNumber)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              cursor: 'pointer',
            }}
          >
            <Typography
              variant='body1'
              sx={{ color: '#1976d2', textDecoration: 'underline' }}
            >
              {renderedCellValue}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {`Item #: ${itemLabel} | UPC #: ${upcLabel}`}
            </Typography>
          </Box>
        );
      },
    },
    {
      header: 'Cost',
      accessorKey: 'cost',
      size: 100,
      Cell: ({ renderedCellValue, row }) => {
        const onSale = row.original.on_sale;
        let textColor = 'inherit';
        let backgroundColor = 'transparent';
        if (onSale) {
          textColor = green[700];
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor,
              p: 1,
              borderRadius: 1,
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant='body1'
              sx={{ fontWeight: onSale ? 'bold' : 'normal', color: textColor }}
            >
              ${renderedCellValue}
            </Typography>
            {onSale && (
              <Tooltip title='On Sale!' placement='right'>
                <LocalOfferIcon sx={{ ml: 1, color: green[700] }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      header: 'SS Item #',
      accessorKey: 'item_number',
      size: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: true,
      enableHiding: true,
      Cell: () => null,
    },
  ];
};

export default getColumns;
